/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/components/ToolPanel/ProtocolValue
 * @desc Menu to select an inventory field to add to the Smart Table
 */

import { EntitySearchMulti } from 'labstep-web/components/Entity/Search/Multi';
import { ICONS } from 'labstep-web/constants/icons';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { createOrUpdateValue } from 'labstep-web/core/DataGrid/utils';
import { Flex } from 'labstep-web/core/Flex';
import { Header, SectionHeader } from 'labstep-web/core/Header';
import { EntityDataGridToolPanelListItem } from 'labstep-web/grid/SmartTableProtocolValue/components/ToolPanel/ListItem';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import { GridToolBarService } from 'labstep-web/grid/services/grid-toolbar.service';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { Resource } from 'labstep-web/models/resource.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import type React from 'react';
import type { IEntityDataGridToolPanelProtocolValueProps } from './types';

export const EntityDataGridToolPanelProtocolValue: React.FC<
  IEntityDataGridToolPanelProtocolValueProps
> = ({
  entityView,
  gridSmartTableProtocolValueService,
  colIds,
  experimentOrProtocol,
}) => {
  const protocolValueNameFilter =
    GridSmartTableProtocolValueService.getEntityViewProtocolValueNameFilter(
      entityView,
    );
  const selectedProtocolValues = protocolValueNameFilter
    ? experimentOrProtocol.protocol_values.filter(
        (protocolValue: ProtocolValue) =>
          (
            (protocolValueNameFilter.value as string[]) || []
          ).includes(protocolValue.reportingName),
      )
    : [];

  return (
    <div data-cy="grid/SmartTableProtocolValue/components/ToolPanel/AddColumn">
      <Flex vAlignContent="center" spacing="gap" shrink={0}>
        <Header size="tiny" noMargin>
          Add Inventory Field
        </Header>
      </Flex>
      <small>
        Select which inventory field to show in the table.
      </small>
      <EntitySearchMulti
        staticLists={(searchQuery) => (
          <>
            <SectionHeader
              label={`Inventory Fields in this ${getHumanReadableEntityName(experimentOrProtocol.entityName)}`}
            />
            {experimentOrProtocol.protocol_values.map(
              (protocolValue) => {
                if (
                  !GridToolBarService.showListItem(
                    protocolValue.reportingName,
                    searchQuery,
                  )
                ) {
                  return;
                }
                return (
                  <EntityDataGridToolPanelListItem
                    icon={ICONS.resource.primary}
                    actionIcon="eye"
                    text={protocolValue.reportingName}
                    onClick={() => {
                      if (
                        selectedProtocolValues.find(
                          (selected) =>
                            selected.id === protocolValue.id,
                        )
                      ) {
                        GridSmartTableProtocolValueService.unselectProtocolValue(
                          entityView,
                          protocolValue,
                        );
                      } else {
                        GridSmartTableProtocolValueService.selectProtocolValue(
                          entityView,
                          protocolValue,
                        );
                      }
                    }}
                    disabled={
                      !!selectedProtocolValues.find(
                        (selected) =>
                          selected.id === protocolValue.id,
                      )
                    }
                  />
                );
              },
            )}
          </>
        )}
        lists={[
          {
            key: 'resource',
            entityName: Resource.entityName,
            searchField: 'search_query',
            params: {
              group_id: entityView.group.id,
              count: 8,
            },
            header: (
              <SectionHeader label="Add New Field for Specific Resource" />
            ),
            listItem: (entity: object) => {
              return (
                <EntityDataGridToolPanelListItem
                  icon={ICONS.resource.primary}
                  text={(entity as Resource).displayName}
                  onClick={() => {
                    createOrUpdateValue({
                      entityName: ProtocolValue.entityName,
                      body: {
                        name: (entity as Resource).name,
                        resource_id: (entity as Resource).id,
                        [`${experimentOrProtocol.entityName}_id`]:
                          experimentOrProtocol.guid,
                      },
                      options: {
                        onSuccess: ({ response }: any): void => {
                          const { result, entities } = response;
                          const protocolValue = new ProtocolValue({
                            ...entities.protocol_value[`${result}`],
                          });
                          GridSmartTableProtocolValueService.selectProtocolValue(
                            entityView,
                            protocolValue,
                          );
                        },
                      },
                    });
                  }}
                />
              );
            },
          },
          {
            key: 'resource_template',
            entityName: Resource.entityName,
            searchField: 'search_query',
            params: {
              group_id: entityView.group.id,
              is_template: true,
              count: 8,
            },
            header: (
              <SectionHeader label="Add New Field for Specific Category" />
            ),
            listItem: (entity: object) => {
              return (
                <EntityDataGridToolPanelListItem
                  icon={ICONS.resource.template}
                  text={(entity as Resource).displayName}
                  onClick={() => {
                    createOrUpdateValue({
                      entityName: ProtocolValue.entityName,
                      body: {
                        name: (entity as Resource).name,
                        resource_template_id: (entity as Resource).id,
                        [`${experimentOrProtocol.entityName}_id`]:
                          experimentOrProtocol.guid,
                      },
                      options: {
                        onSuccess: ({ response }: any): void => {
                          const { result, entities } = response;
                          const protocolValue = new ProtocolValue({
                            ...entities.protocol_value[`${result}`],
                          });
                          GridSmartTableProtocolValueService.selectProtocolValue(
                            entityView,
                            protocolValue,
                          );
                        },
                      },
                    });
                  }}
                />
              );
            },
          },
        ]}
        footer={(searchQuery): JSX.Element => (
          <>
            <SectionHeader label="Add Blank Inventory Field" />
            <ActionComponent
              icon="plus"
              type="text"
              text={
                searchQuery
                  ? `Add New Inventory Field: "${searchQuery}"`
                  : 'Add New Inventory Field'
              }
              onClick={(): void => {
                createOrUpdateValue({
                  entityName: ProtocolValue.entityName,
                  body: {
                    name: searchQuery,
                    [`${experimentOrProtocol.entityName}_id`]:
                      experimentOrProtocol.guid,
                  },
                  options: {
                    onSuccess: ({ response }: any): void => {
                      const { result, entities } = response;
                      const protocolValue = new ProtocolValue({
                        ...entities.protocol_value[`${result}`],
                      });
                      GridSmartTableProtocolValueService.selectProtocolValue(
                        entityView,
                        protocolValue,
                      );
                    },
                  },
                });
              }}
            />
          </>
        )}
      />
    </div>
  );
};
